// Libs
import clsx from "clsx";
import type { FC, ReactNode } from "react";
import { NavLink as RouterLink, useLocation } from "react-router-dom";

// Utils
import type { Theme } from "src/theme";

// Components
import { Box, Button, Collapse, ListItem } from "@mui/material";
import { makeStyles } from "@mui/styles";

interface NavItemProps {
    children?: ReactNode;
    className?: string;
    depth: number;
    href?: string;
    icon?: any;
    styledIcon?: JSX.Element;
    info?: any;
    open?: boolean;
    title: string;
    disabled?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
    item: {
        display: "block",
        paddingTop: 0,
        paddingBottom: 0,
    },
    itemLeaf: {
        display: "flex",
        paddingTop: "0 !important",
    },
    button: {
        color: theme.palette.text.secondary,
        padding: "10px 8px",
        justifyContent: "flex-start",
        textTransform: "none",
        letterSpacing: 0,
        width: "100%",
        "&.Mui-disabled": {
            color: theme.palette.text.secondary,
            padding: "10px 8px",
            justifyContent: "flex-start",
            textTransform: "none",
            letterSpacing: 0,
            width: "100%",
            textDecoration: "underline",
        },
    },
    buttonLeaf: {
        color: theme.palette.text.secondary,
        padding: "10px 8px",
        justifyContent: "flex-start",
        textTransform: "none",
        letterSpacing: 0,
        width: "100%",
        fontWeight: 400,
        "&.depth-0": {
            "& $title": {
                fontWeight: 500,
            },
        },
    },
    icon: {
        display: "flex",
        alignItems: "center",
        marginRight: theme.spacing(1),
    },
    title: {
        marginRight: "auto",
        whiteSpace: "pre-line",
    },
    active: {
        color: theme.palette.secondary.main,
    },
}));

const NavItem: FC<NavItemProps> = ({
    children,
    className,
    depth,
    href,
    icon: Icon,
    styledIcon,
    info: Info,
    title,
    disabled,
    ...rest
}) => {
    const classes = useStyles();
    const { pathname } = useLocation();

    let paddingLeft = 8;

    if (depth > 0) {
        paddingLeft = 32 + 8 * depth;
    }

    const style = { paddingLeft };

    if (children) {
        return (
            <ListItem
                className={clsx(classes.item, className)}
                disableGutters
                key={title}
                {...rest}
            >
                <Button className={classes.button} style={style} disabled>
                    {Icon && <Icon className={classes.icon} size="20" />}
                    {Boolean(styledIcon) && styledIcon}
                    <span className={classes.title}>{title}</span>
                    {/* {open ? <ExpandLessIcon /> : <ExpandMoreIcon />} */}
                </Button>
                <Collapse in={true}>{children}</Collapse>
            </ListItem>
        );
    }

    if (disabled === true) {
        return (
            <ListItem
                className={clsx(classes.itemLeaf, className)}
                disableGutters
                key={title}
                {...rest}
            >
                <Box
                    className={clsx(classes.buttonLeaf, `depth-${depth}`)}
                    style={{ ...style, display: "flex", cursor: "not-allowed", color: "#C2C7D4" }}
                >
                    {Icon && <Icon className={classes.icon} size="20" />}
                    {Boolean(styledIcon) && styledIcon}
                    <div className={classes.title}>{title}</div>
                    {Info && <Info />}
                </Box>
            </ListItem>
        );
    }

    const isActive = pathname === href || pathname.startsWith(`${href}/`);
    return (
        <ListItem
            className={clsx(classes.itemLeaf, className)}
            disableGutters
            key={title}
            {...rest}
        >
            <Button
                activeClassName={classes.active}
                sx={{ color: isActive ? "#394DFF" : "black", fontSize: 14, textTransform: "none" }}
                className={clsx(classes.buttonLeaf, `depth-${depth}`)}
                component={RouterLink}
                exact
                style={style}
                to={href}
            >
                {Icon && <Icon className={classes.icon} size="20" />}
                {Boolean(styledIcon) && styledIcon}
                <div className={classes.title}>{title}</div>
                {Info && <Info />}
            </Button>
        </ListItem>
    );
};

export default NavItem;
